import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type { SearchQuestionsInput } from '../../domain/entities/content.entity';

/**
 * A busca de questões do admin (DESEN-42/43): só questão publicada. Mantém a
 * página anterior enquanto a nova carrega, para a lista não piscar a cada
 * filtro.
 *
 * `enabled: false` enquanto a tela ainda confere os filtros da URL: sem isso,
 * um assunto ou uma instituição que não existe iria ao back antes de as
 * opções chegarem para descartá-lo.
 */
export function useQuestionSearch(input: SearchQuestionsInput, enabled = true) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'questions', 'search', input],
    queryFn: () => repository.searchQuestions(input),
    placeholderData: keepPreviousData,
    // Sem cache entre visitas: quem resolve o report na ficha e volta pelo
    // "Voltar" precisa ver a lista de agora, e nada invalida esta chave.
    staleTime: 0,
    enabled,
  });
}

/**
 * As opções de um nível do assunto: `'root'` pede as Grandes Áreas (e os
 * anos); um id pede os filhos dele; `''` é o nível sem pai, que não pede nada.
 *
 * O nível sem pai tem chave própria de propósito: se ele dividisse a chave
 * com a raiz, a Especialidade sem Grande Área leria do cache as Grandes Áreas.
 */
function useQuestionFilterOptions(parent: string) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'question-filter-options', parent || 'none'],
    queryFn: () =>
      repository.getQuestionFilterOptions(
        parent === 'root' ? undefined : parent,
      ),
    enabled: parent !== '',
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

/**
 * Os três níveis do assunto em cascata: a Especialidade espera a Grande Área,
 * e o Tema espera a Especialidade. O Ano sai das opções da raiz. A tela e o
 * filtro chamam com os mesmos pais, e o react-query divide as consultas.
 */
export function useQuestionFilterCascade(parents: {
  area: string;
  specialty: string;
}) {
  return {
    areas: useQuestionFilterOptions('root'),
    specialties: useQuestionFilterOptions(parents.area),
    themes: useQuestionFilterOptions(parents.area && parents.specialty),
  };
}
